/**
 * HellaBots Widget Embed Script
 *
 * Usage:
 * <script src="https://your-domain.azurestaticapps.net/widget.js"></script>
 * <script>
 *   HellaBots.init({ clientId: 'your-client-id' })
 * </script>
 */

import React from 'react'
import { createRoot } from 'react-dom/client'
import { ChatWidget, ChatWidgetProps } from './components/ChatWidget'

// Global interface
interface HellaBotsGlobal {
  init: (config: { clientId: string; containerId?: string; position?: 'bottom-right' | 'bottom-left' }) => void
}

// Create global HellaBots object
const HellaBots: HellaBotsGlobal = {
  init: (config) => {
    const { clientId, containerId, position } = config

    // Wait for DOM to be ready
    const initWidget = () => {
      let container: HTMLElement

      if (containerId) {
        // Mount to specific container
        const existingContainer = document.getElementById(containerId)
        if (!existingContainer) {
          console.error(`HellaBots: Container #${containerId} not found`)
          return
        }
        container = existingContainer
      } else {
        // Create floating widget container
        container = document.createElement('div')
        container.id = 'hellabots-widget-root'
        document.body.appendChild(container)
      }

      // Render widget
      const root = createRoot(container)
      const widgetProps: ChatWidgetProps = {
        clientId,
        ...(containerId && { containerId }),
        ...(position && { position }),
      }

      root.render(React.createElement(ChatWidget, widgetProps))

      console.log(`✅ HellaBots widget initialized for client: ${clientId}`)
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWidget)
    } else {
      initWidget()
    }
  },
}

// Expose to window
declare global {
  interface Window {
    HellaBots: HellaBotsGlobal
  }
}

window.HellaBots = HellaBots

// Auto-init if data-client-id attribute exists on script tag
const currentScript = document.currentScript as HTMLScriptElement | null
if (currentScript) {
  const autoClientId = currentScript.getAttribute('data-client-id')
  if (autoClientId) {
    HellaBots.init({ clientId: autoClientId })
  }
}

export default HellaBots
